import { MenuItem, TextField } from '@mui/material';
import { useState } from 'react';

import { createBatch, updateBatch } from '../../api/business/endpoints';
import type { Batch, ProductDetail } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { moneyInputValue, parseMoneyInput } from '../../components/parse';
import { isVariable, receivingVariants, variantLabel } from '../../components/variants';
import { useT } from '../../i18n/LanguageContext';

type BatchDialogProps = {
  companyId: string;
  product: ProductDetail;
  currency: string;
  /** null creates a batch; a batch edits its prices — expiry and lot are its identity and stay fixed. */
  batch: Batch | null;
  onClose: () => void;
};

export const BatchDialog = ({ companyId, product, currency, batch, onClose }: BatchDialogProps) => {
  const t = useT();
  const productId = product.id;
  // A variable product's new batch belongs to a variant the user chooses — the API refuses to guess (ADR-0010).
  const variable = isVariable(product);
  const choices = receivingVariants(product);
  const [variantId, setVariantId] = useState(choices.length === 1 ? choices[0].id : '');
  const [expiryDate, setExpiryDate] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [price, setPrice] = useState(moneyInputValue(batch?.priceMinor ?? null));
  const [purchasePrice, setPurchasePrice] = useState(moneyInputValue(batch?.purchasePriceMinor ?? null));
  const [problem, setProblem] = useState<string | null>(null);

  const mutation = useAppMutation(
    (prices: { priceMinor: number | null; purchasePriceMinor: number | null }) =>
      batch
        ? updateBatch(companyId, productId, batch.id, prices)
        : createBatch(companyId, productId, {
            variantId: variable ? variantId : undefined,
            expiryDate: expiryDate || undefined,
            lotNumber: lotNumber.trim() || undefined,
            priceMinor: prices.priceMinor ?? undefined,
            purchasePriceMinor: prices.purchasePriceMinor ?? undefined,
          }),
    ['product', 'products'],
    onClose,
  );

  const submit = (): void => {
    const priceMinor = parseMoneyInput(price);
    const purchasePriceMinor = parseMoneyInput(purchasePrice);

    if (priceMinor === undefined || purchasePriceMinor === undefined) {
      setProblem(t('money.invalid'));

      return;
    }

    if (!batch && variable && variantId === '') {
      setProblem(t('stock.variantRequired'));

      return;
    }

    setProblem(null);
    mutation.mutate({ priceMinor, purchasePriceMinor });
  };

  return (
    <FormDialog
      title={batch ? t('batches.edit') : t('batches.create')}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={batch ? t('common.save') : t('common.create')}
      isBusy={mutation.isPending}
      error={mutation.error}
      problem={problem}
    >
      {batch ? null : (
        <>
          {variable ? (
            <TextField
              select
              required
              label={t('field.variant')}
              value={variantId}
              onChange={(e) => setVariantId(e.target.value)}
            >
              {choices.map((variant) => (
                <MenuItem key={variant.id} value={variant.id}>
                  {variantLabel(variant, t)}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <TextField
            type="date"
            label={t('field.expiryDate')}
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={t('field.lotNumber')}
            value={lotNumber}
            onChange={(e) => setLotNumber(e.target.value)}
          />
        </>
      )}
      <TextField
        label={t('field.price', { currency })}
        helperText={t('batches.priceHint')}
        value={price}
        inputProps={{ inputMode: 'decimal' }}
        onChange={(e) => setPrice(e.target.value)}
      />
      <TextField
        label={t('field.purchasePrice', { currency })}
        value={purchasePrice}
        inputProps={{ inputMode: 'decimal' }}
        onChange={(e) => setPurchasePrice(e.target.value)}
      />
    </FormDialog>
  );
};
